/**
 * Kundprojekt – projekt som en kund driver eller deltar i, registrerade som
 * säljunderlag i Affärer.
 *
 * INTE Tikabs egna uppdrag (`projects`, UppdragN) eller referensprojekt
 * (`referenceProjects`, `crmCompanies/{id}/references`). Egen samling,
 * egna konstanter, heter alltid "Kundprojekt" i UI:t.
 *
 * Ren logik utan Firebase: värdelistor, tomt kundprojekt, normalisering,
 * säkerhetsregeln och beräkning av de avledda fälten. Läsning och skrivning
 * i kundprojektData.js.
 *
 * Samling `kundprojekt/{id}`:
 *   namn*, beskrivning, ort (kommun), typ, byggskede, skedeStart ('YYYY-MM'),
 *   nastaSkede, entreprenadform, storleksklass, driftkrav, verktygIdag,
 *   antalGranskare, status, kalla, registreradVid { leadId, aktivitetId },
 *   externKalla { system, externtId, url, hamtad }, skapadAv, skapad, uppdaterad
 *   Säkerhet: sakerhetsskydd, itMiljo, projektkontor, sakerhetsskyddsavtal,
 *     externaParterBehoverAtkomst, filoverforing, granskningIdag
 *   parter: [{ kundId, roll, kontakter: [{ kontaktId, rollIProjektet }], leadIds: [] }]
 *   testkorningar: { [leadId]: { start, slut, ansvarigKontaktId } } – per
 *     koppling lead↔kundprojekt (frivilligt; tas bort när kopplingen tas bort)
 *
 * Nya fält på crmCompanies: erfarenhetSakerhetsskydd, harIsoleradMiljo,
 * sakerhetsAnteckning. Avledda fält (hålls i synk av kundprojektData.js):
 * kundprojektIds[] på kund, kontakt och lead.
 *
 * Värden lagras som ASCII-nycklar, etiketterna visas i UI:t.
 */

const v = (key, label) => ({ key, label });

export const KP_TYPER = [
  v('nybyggnad', 'Nybyggnad'),
  v('ombyggnad', 'Ombyggnad'),
  v('anlaggning', 'Anläggning/infrastruktur'),
  v('industri', 'Industri'),
  v('ovrigt', 'Övrigt'),
];

export const KP_BYGGSKEDEN = [
  v('forstudie', 'Förstudie'),
  v('programhandling', 'Programhandling'),
  v('systemhandling', 'Systemhandling'),
  v('bygghandling', 'Bygghandling'),
  v('produktion', 'Produktion'),
  v('forvaltning', 'Förvaltning'),
  v('okant', 'Okänt'),
];

export const KP_ENTREPRENADFORMER = [
  v('utforande', 'Utförandeentreprenad'),
  v('total', 'Totalentreprenad'),
  v('general', 'Generalentreprenad'),
  v('delad', 'Delad entreprenad'),
  v('samverkan', 'Samverkan/partnering'),
  v('okant', 'Okänt'),
];

export const KP_STORLEKSKLASSER = [
  v('under50', '< 50 MSEK'),
  v('50-150', '50–150 MSEK'),
  v('150-500', '150–500 MSEK'),
  v('over500', '> 500 MSEK'),
  v('okant', 'Okänt'),
];

export const KP_DRIFTKRAV = [v('onprem', 'On-prem'), v('saas', 'SaaS'), v('okant', 'Okänt')];

export const KP_STATUSAR = [v('aktivt', 'Aktivt'), v('vilande', 'Vilande'), v('avslutat', 'Avslutat')];

export const KP_KALLOR = [
  v('samtal', 'Samtal'), v('mote', 'Möte'), v('mejl', 'Mejl'), v('webb', 'Webb'),
  v('projektdatabas', 'Projektdatabas'), v('annat', 'Annat'),
];

export const JA_NEJ_OKANT = [v('ja', 'Ja'), v('nej', 'Nej'), v('okant', 'Okänt')];

export const KP_IT_MILJOER = [
  v('airgappad', 'Airgappad/isolerat nät'),
  v('onprem_kund', 'On-prem i kundens miljö'),
  v('godkand_moln', 'Godkänd molntjänst'),
  v('blandat', 'Blandat'),
  v('okant', 'Okänt'),
];

export const KP_PROJEKTKONTOR = [
  v('separat', 'Separat skyddat projektkontor'),
  v('delvis', 'Delvis'),
  v('nej', 'Nej'),
  v('okant', 'Okänt'),
];

export const KP_FILOVERFORING = [
  v('fysiskt', 'USB/fysiskt medium'),
  v('saker', 'Säker filöverföring'),
  v('ingen', 'Ingen'),
  v('okant', 'Okänt'),
];

export const KP_ROLLER = [
  v('byggherre', 'Byggherre/beställare'),
  v('totalentreprenor', 'Totalentreprenör'),
  v('generalentreprenor', 'Generalentreprenör'),
  v('entreprenor', 'Entreprenör/UE'),
  v('projektor', 'Projektör/konsult'),
  v('bim', 'BIM-samordnare'),
  v('forvaltare', 'Förvaltare'),
  v('ovrigt', 'Övrigt'),
];

/** Etikett för ett lagrat värde, eller '' om det saknas. */
export function etikett(lista, key) {
  return lista.find(x => x.key === key)?.label || '';
}

// ── Säkerhet ────────────────────────────────────────────────────────────────

/** Visas vid alla fritextfält i säkerhetsskyddade kundprojekt och vid kundens sakerhetsAnteckning. */
export const SAKERHET_VARNING =
  'Skriv inga uppgifter som omfattas av säkerhetsskydd (t.ex. objekt, funktion, exakt plats eller tekniska detaljer).';

/** Visas vid namnet när kundprojektet är säkerhetsskyddat. */
export const SAKERHET_NAMN_HJALP = 'Säkerhetsskyddat kundprojekt: använd ett allmänt namn eller kodnamn.';

/** Säkerhetsfälten utom sakerhetsskydd, med värdet de får när de nollställs. */
export const SAKERHETSFALT = {
  itMiljo: 'okant',
  projektkontor: 'okant',
  sakerhetsskyddsavtal: 'okant',
  externaParterBehoverAtkomst: 'okant',
  filoverforing: 'okant',
  granskningIdag: '',
};

/** Fritextfälten på ett kundprojekt (varningen visas vid dem när det är säkerhetsskyddat). */
export const KP_FRITEXTFALT = ['namn', 'beskrivning', 'verktygIdag', 'granskningIdag'];

/** Säkerhetssektionen visas bara när sakerhetsskydd är ja eller okänt. */
export function visaSakerhetsfalt(kp) {
  return kp?.sakerhetsskydd === 'ja' || kp?.sakerhetsskydd === 'okant';
}

/** Varningen visas vid fritextfälten när kundprojektet är säkerhetsskyddat (ja eller okänt). */
export function visaSakerhetsvarning(kp) {
  return visaSakerhetsfalt(kp);
}

export const arIsoleradMiljo = kp => kp?.itMiljo === 'airgappad';
export const arSakerhetsskyddat = kp => kp?.sakerhetsskydd === 'ja';

// ── Tomt kundprojekt och normalisering ──────────────────────────────────────

export function tomtKundprojekt(extra = {}) {
  return {
    namn: '', beskrivning: '', ort: '',
    typ: '', byggskede: 'okant', skedeStart: '', nastaSkede: '',
    entreprenadform: 'okant', storleksklass: 'okant', driftkrav: 'okant',
    verktygIdag: '', antalGranskare: null,
    status: 'aktivt', kalla: '', registreradVid: null, externKalla: null,
    sakerhetsskydd: 'okant', ...SAKERHETSFALT,
    parter: [],
    testkorningar: {},
    ...extra,
  };
}

/** En part. kontakter: [{ kontaktId, rollIProjektet }] */
export function nyPart(kundId, roll = '', extra = {}) {
  return { kundId, roll, kontakter: [], leadIds: [], ...extra };
}

const unika = list => [...new Set((list || []).filter(Boolean))];

/**
 * Det som sparas: trimmade texter, rensade parter (dubbla kontakter och leads
 * bort, parter utan kund bort) och säkerhetsfälten nollställda när
 * sakerhetsskydd är nej – då ska inget säkerhetsunderlag ligga kvar.
 */
export function normaliseraKundprojekt(kp) {
  const ut = { ...tomtKundprojekt(), ...kp };
  for (const k of ['namn', 'beskrivning', 'ort', 'verktygIdag', 'granskningIdag']) {
    ut[k] = String(ut[k] ?? '').trim();
  }
  const n = Number(ut.antalGranskare);
  ut.antalGranskare = ut.antalGranskare === '' || ut.antalGranskare == null || !Number.isFinite(n) ? null : Math.max(0, Math.round(n));
  if (ut.sakerhetsskydd === 'nej') Object.assign(ut, SAKERHETSFALT);
  ut.parter = (ut.parter || [])
    .filter(p => p && p.kundId)
    .map(p => {
      const sedda = new Set();
      return {
        kundId: p.kundId,
        roll: p.roll || '',
        kontakter: (p.kontakter || []).filter(k => {
          if (!k?.kontaktId || sedda.has(k.kontaktId)) return false;
          sedda.add(k.kontaktId);
          return true;
        }).map(k => ({ kontaktId: k.kontaktId, rollIProjektet: String(k.rollIProjektet || '').trim() })),
        leadIds: unika(p.leadIds),
      };
    });
  ut.testkorningar = normaliseraTestkorningar(ut.testkorningar, kopplingar(ut).leads);
  return ut;
}

/**
 * Testkörningar finns bara för leads som är kopplade. Tomma poster tas bort.
 * Datum som 'YYYY-MM-DD'.
 */
export function normaliseraTestkorningar(testkorningar, kopplade) {
  const ut = {};
  for (const [leadId, t] of Object.entries(testkorningar || {})) {
    if (!kopplade.has(leadId) || !t) continue;
    const post = {
      start: String(t.start || '').slice(0, 10),
      slut: String(t.slut || '').slice(0, 10),
      ansvarigKontaktId: t.ansvarigKontaktId || '',
    };
    if (post.start || post.slut || post.ansvarigKontaktId) ut[leadId] = post;
  }
  return ut;
}

// ── Redigering: ändrade fält ────────────────────────────────────────────────

/** Fält som inte redigeras i formuläret och aldrig skrivs över av det. */
const SYSTEMFALT = ['id', 'skapad', 'skapadAv', 'uppdaterad', 'uppdateradAv', 'externKalla'];

const lika = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Fälten som skiljer mellan två versioner av ett kundprojekt, båda
 * normaliserade. Används för att bara skriva det som ändrats, så att
 * senaste ändring per fält vinner när två personer redigerar samtidigt.
 */
export function andradeFalt(fore, efter) {
  const a = normaliseraKundprojekt(fore || {});
  const b = normaliseraKundprojekt(efter || {});
  const nycklar = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...nycklar].filter(k => !SYSTEMFALT.includes(k) && !lika(a[k], b[k])).sort();
}

/**
 * Lägger användarens ändringar (relativt det som öppnades) ovanpå den
 * aktuella versionen i databasen. Fält användaren inte rört kommer från
 * den aktuella versionen.
 */
export function slaIhop(aktuell, oppnad, redigerad) {
  const egna = andradeFalt(oppnad, redigerad);
  const r = normaliseraKundprojekt(redigerad);
  const ut = { ...aktuell };
  for (const k of egna) ut[k] = r[k];
  return { kundprojekt: normaliseraKundprojekt(ut), egna };
}


/** Fel som stoppar sparandet, som text. Tom lista = OK. */
export function valideraKundprojekt(kp) {
  const fel = [];
  if (!String(kp?.namn || '').trim()) fel.push('Namn saknas.');
  if (!(kp?.parter || []).some(p => p?.kundId)) fel.push('Minst en part (kund) behövs.');
  if (kp?.skedeStart && !/^\d{4}-(0[1-9]|1[0-2])$/.test(kp.skedeStart)) fel.push('Skedestart ska vara en månad (ÅÅÅÅ-MM).');
  return fel;
}

// ── Avledda fält ────────────────────────────────────────────────────────────

/** Nyckel för en kontakt: kontakter ligger under sin kund. */
export const kontaktNyckel = (kundId, kontaktId) => `${kundId}/${kontaktId}`;

/** { kunder, kontakter, leads } som kundprojektet pekar på, som Set. */
export function kopplingar(kp) {
  const kunder = new Set();
  const kontakter = new Set();
  const leads = new Set();
  for (const p of kp?.parter || []) {
    if (!p?.kundId) continue;
    kunder.add(p.kundId);
    for (const k of p.kontakter || []) if (k?.kontaktId) kontakter.add(kontaktNyckel(p.kundId, k.kontaktId));
    for (const l of p.leadIds || []) if (l) leads.add(l);
  }
  return { kunder, kontakter, leads };
}

const skillnad = (a, b) => [...a].filter(x => !b.has(x));

/**
 * Vilka avledda kundprojektIds som ska läggas till och tas bort när ett
 * kundprojekt går från `fore` (null = nytt) till `efter` (null = raderat).
 * @returns {{ kunder: {lagg, ta}, kontakter: {lagg, ta}, leads: {lagg, ta} }}
 *   kontakter som 'kundId/kontaktId'
 */
export function synkAndringar(fore, efter) {
  const a = kopplingar(fore);
  const b = kopplingar(efter);
  const del = nyckel => ({ lagg: skillnad(b[nyckel], a[nyckel]), ta: skillnad(a[nyckel], b[nyckel]) });
  return { kunder: del('kunder'), kontakter: del('kontakter'), leads: del('leads') };
}

/**
 * Kundflaggor som ska sättas till 'ja' automatiskt. En kund får flaggan när
 * den blir part i ett kundprojekt med flaggan, eller när ett kundprojekt där
 * den redan är part får flaggan. Flaggorna sätts aldrig tillbaka automatiskt,
 * och en manuell ändring skrivs inte över när kundprojektet sparas igen utan
 * att något av det ändrats.
 * @returns {{ [kundId]: { erfarenhetSakerhetsskydd?: 'ja', harIsoleradMiljo?: 'ja' } }}
 */
export function kundflaggor(fore, efter) {
  const ut = {};
  if (!efter) return ut;
  const foreKunder = kopplingar(fore).kunder;
  const regler = [
    ['erfarenhetSakerhetsskydd', arSakerhetsskyddat],
    ['harIsoleradMiljo', arIsoleradMiljo],
  ];
  for (const kundId of kopplingar(efter).kunder) {
    const nyPartIProjektet = !foreKunder.has(kundId);
    for (const [falt, villkor] of regler) {
      if (!villkor(efter)) continue;
      if (nyPartIProjektet || !villkor(fore)) (ut[kundId] ||= {})[falt] = 'ja';
    }
  }
  return ut;
}

// ── Koppla lead ─────────────────────────────────────────────────────────────

/** Första parten för kunden, eller null. */
export function partFor(kp, kundId) {
  return (kp?.parter || []).find(p => p.kundId === kundId) || null;
}

/** Kontaktens roll i projektet, eller ''. */
export function kontaktensRoll(kp, kundId, kontaktId) {
  for (const p of kp?.parter || []) {
    if (p.kundId !== kundId) continue;
    const k = (p.kontakter || []).find(x => x.kontaktId === kontaktId);
    if (k) return k.rollIProjektet || '';
  }
  return '';
}

/**
 * Kopplar leadet till kundprojektet, på leadets kunds part. Är kunden inte
 * part än läggs den till (utan roll), så att kopplingen blir synlig även på
 * kundkortet.
 */
export function medLead(kp, leadId, kundId) {
  const parter = [...(kp.parter || [])];
  const i = parter.findIndex(p => p.kundId === kundId);
  if (i === -1) parter.push(nyPart(kundId, '', { leadIds: [leadId] }));
  else parter[i] = { ...parter[i], leadIds: [...new Set([...(parter[i].leadIds || []), leadId])] };
  return { ...kp, parter };
}

/** Tar bort leadet från alla parter. Parterna ligger kvar. */
export function utanLead(kp, leadId) {
  return { ...kp, parter: (kp.parter || []).map(p => ({ ...p, leadIds: (p.leadIds || []).filter(x => x !== leadId) })) };
}

// ── Pipeline: sammanfattning och filter ─────────────────────────────────────

const harLead = kp => (kp?.parter || []).some(p => (p.leadIds || []).length);

function millis(val) {
  if (!val) return null;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (typeof val.seconds === 'number') return val.seconds * 1000;
  const t = new Date(val).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Siffrorna i Pipeline-sidans kundprojektrad. Räknar aktiva kundprojekt,
 * utom "nya", som räknar alla registrerade sedan månadens början.
 */
export function kundprojektSammanfattning(lista, now = Date.now()) {
  const aktiva = lista.filter(kp => (kp.status || 'aktivt') === 'aktivt');
  const d = new Date(now);
  const manadStart = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
  return {
    totalt: lista.length,
    aktiva: aktiva.length,
    medLead: aktiva.filter(harLead).length,
    sakerhetsskyddade: aktiva.filter(arSakerhetsskyddat).length,
    isolerad: aktiva.filter(arIsoleradMiljo).length,
    nyaDennaManad: lista.filter(kp => (millis(kp.skapad) ?? 0) >= manadStart).length,
  };
}

/**
 * Kundprojektfiltret på tavlan och i listan. Samma nycklar i dropdownen
 * "Kundprojekt" och i de klickbara siffrorna i kundprojektraden.
 * kort: texten i chip-raden ("Kundprojekt: säkerhetsskyddat").
 */
export const KP_LEADFILTER = [
  { key: 'har',      label: 'Har kundprojekt',    kort: 'har kundprojekt' },
  { key: 'saknar',   label: 'Saknar kundprojekt', kort: 'saknar kundprojekt' },
  { key: 'skydd',    label: 'Säkerhetsskyddat',   kort: 'säkerhetsskyddat' },
  { key: 'isolerad', label: 'Isolerad miljö',     kort: 'isolerad miljö' },
  { key: 'aktiva',   label: 'Aktivt kundprojekt', kort: 'aktivt kundprojekt' },
];

/**
 * Predikat för leads. kpMap: { [kundprojektId]: kundprojekt }
 *   har      leadet har kundprojektIds
 *   saknar   leadet har inga kundprojektIds
 *   aktiva   minst ett kopplat kundprojekt med status aktivt
 *   skydd    minst ett säkerhetsskyddat (sakerhetsskydd = ja)
 *   isolerad minst ett med itMiljo = airgappad/isolerat nät
 */
export function kpLeadPredikat(key, kpMap) {
  const ids = l => l.kundprojektIds || [];
  const kps = l => ids(l).map(id => kpMap[id]).filter(Boolean);
  if (key === 'har') return l => ids(l).length > 0;
  if (key === 'saknar') return l => ids(l).length === 0;
  if (key === 'aktiva') return l => kps(l).some(kp => (kp.status || 'aktivt') === 'aktivt');
  if (key === 'skydd') return l => kps(l).some(arSakerhetsskyddat);
  if (key === 'isolerad') return l => kps(l).some(arIsoleradMiljo);
  return () => true;
}

const MANADER = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** 'YYYY-MM' → 'mar 2027', annars ''. */
export function manadText(skedeStart) {
  const m = /^(\d{4})-(\d{2})$/.exec(skedeStart || '');
  return m ? `${MANADER[Number(m[2]) - 1]} ${m[1]}` : '';
}

// ── Kundprojektvyn i Affärer ────────────────────────────────────────────────

/** Antal månader från innevarande månad till skedeStart ('YYYY-MM'), eller null. */
export function manaderTill(skedeStart, now = Date.now()) {
  const m = /^(\d{4})-(\d{2})$/.exec(skedeStart || '');
  if (!m) return null;
  const d = new Date(now);
  return (Number(m[1]) - d.getFullYear()) * 12 + (Number(m[2]) - 1 - d.getMonth());
}

/** Skeden där granskningen pågår eller står för dörren. */
export const GRANSKNINGSSKEDEN = ['programhandling', 'systemhandling'];
export const GRANSKNING_INOM_MANADER = 3;

/**
 * Snabbvyn "Kommande granskningsfas": aktiva kundprojekt i programhandling
 * eller systemhandling, eller med skedestart inom 3 månader.
 */
export function arKommandeGranskningsfas(kp, now = Date.now()) {
  if ((kp.status || 'aktivt') !== 'aktivt') return false;
  if (GRANSKNINGSSKEDEN.includes(kp.byggskede)) return true;
  const m = manaderTill(kp.skedeStart, now);
  return m != null && m >= 0 && m <= GRANSKNING_INOM_MANADER;
}

export const SKEDESTART_INOM = [
  v('3', 'Inom 3 månader'), v('6', 'Inom 6 månader'), v('12', 'Inom 12 månader'),
];

export const TOMT_KP_FILTER = {
  byggskede: '', skedestartInom: '', entreprenadform: '', roll: '', storleksklass: '',
  sakerhetsskydd: '', isolerad: false, status: '',
};

/** Listvyns filter. Tomma värden filtrerar inte. */
export function filtreraKundprojekt(lista, f = TOMT_KP_FILTER, now = Date.now()) {
  return lista.filter(kp => {
    if (f.byggskede && kp.byggskede !== f.byggskede) return false;
    if (f.skedestartInom) {
      const m = manaderTill(kp.skedeStart, now);
      if (m == null || m < 0 || m > Number(f.skedestartInom)) return false;
    }
    if (f.entreprenadform && kp.entreprenadform !== f.entreprenadform) return false;
    if (f.roll && !(kp.parter || []).some(p => p.roll === f.roll)) return false;
    if (f.storleksklass && kp.storleksklass !== f.storleksklass) return false;
    if (f.sakerhetsskydd && (kp.sakerhetsskydd || 'okant') !== f.sakerhetsskydd) return false;
    if (f.isolerad && !arIsoleradMiljo(kp)) return false;
    if (f.status && (kp.status || 'aktivt') !== f.status) return false;
    return true;
  });
}

/** Skedestart stigande, kundprojekt utan skedestart sist, sedan namn. */
export function sorteraPaSkedestart(lista) {
  return [...lista].sort((a, b) => {
    const sa = a.skedeStart || '9999-99';
    const sb = b.skedeStart || '9999-99';
    return sa.localeCompare(sb) || String(a.namn).localeCompare(String(b.namn), 'sv');
  });
}

/** Rollgrupper i partssammanfattningen. De tre första visas alltid. */
const SAMMANFATTNINGSGRUPPER = [
  { label: 'Byggherre', roller: ['byggherre'], alltid: true },
  { label: 'Entreprenör', roller: ['totalentreprenor', 'generalentreprenor', 'entreprenor'], alltid: true },
  { label: 'Konsult', roller: ['projektor', 'bim'], alltid: true },
  { label: 'Förvaltare', roller: ['forvaltare'] },
  { label: 'Utan roll', roller: ['ovrigt', ''] },
];

/**
 * Sammanfattning per roll, t.ex. "Byggherre: ingen kontakt · Entreprenör:
 * Demo · Konsult: –". Lead → leadets mest framskridna skede; kontakt utan
 * lead → "kontakt, inget lead"; part utan kontakt → "ingen kontakt"; ingen
 * part med rollen → "–".
 * leadMap: { [leadId]: lead }, skede(lead) → { index, label }
 */
export function partSammanfattning(kp, leadMap, skede) {
  const ut = [];
  for (const g of SAMMANFATTNINGSGRUPPER) {
    const parter = (kp.parter || []).filter(p => g.roller.includes(p.roll || ''));
    if (!parter.length) {
      if (g.alltid) ut.push({ label: g.label, text: '–' });
      continue;
    }
    const leads = parter.flatMap(p => p.leadIds || []).map(id => leadMap[id]).filter(Boolean);
    let text;
    if (leads.length) {
      text = leads.map(skede).sort((a, b) => b.index - a.index)[0].label;
    } else if (parter.some(p => (p.kontakter || []).length)) {
      text = 'kontakt, inget lead';
    } else {
      text = 'ingen kontakt';
    }
    ut.push({ label: g.label, text });
  }
  return ut;
}
